// ============================================================================
// Validaciones puras de las entradas de Activos
// ============================================================================
// Las usan igual la pantalla (antes de mandar) y el servicio del API (después
// de `express-validator`, como segunda línea). Devuelven el valor limpio
// (textos recortados, serie normalizada, RFC y UUID en mayúsculas) o la lista
// de errores con su campo y su código; el texto lo pone quien muestra.
//
// En `mode: "update"` solo se valida y se devuelve lo que llegó: el servicio
// combina con el documento y decide. Sin `canSeeCosts` (`feat:activos.costos`)
// los montos se IGNORAN en silencio, como pide el diseño: no es un error.
// ============================================================================

import type {
  AssetCustodian,
  AssetInput,
  InvoiceRef,
  MaintenanceCloseInput,
  MaintenanceOpenInput,
  PurchaseInput,
  RetireInput,
  SupplierInput,
} from "./types";
import type { Label, SupplierRole } from "./vocab";
import {
  BASE_CURRENCY,
  isAssetCategory,
  isMaintenanceKind,
  isRetireReason,
  isSupplierRole,
} from "./vocab";
import { MAX_SERIAL_LENGTH, isValidInvoiceUuid, isValidRfc, normalizeInvoiceUuid, normalizeRfc, normalizeSerial } from "./identifiers";
import { MAX_USEFUL_LIFE_MONTHS, isIsoDate, round2 } from "./depreciation";

// --- Límites -----------------------------------------------------------------------
export const MAX_NAME_LENGTH = 200;
export const MAX_TEXT_LENGTH = 120;
export const MAX_NOTE_LENGTH = 2000;
export const MAX_ID_LENGTH = 128;
/** Un monto por pieza o por factura nunca pasa de esto (evita capturas absurdas). */
export const MAX_AMOUNT = 1_000_000_000;
/**
 * Piezas por compra. Cada pieza escribe activo + candado + evento en la misma
 * transacción, y Firestore admite 500 escrituras por transacción.
 */
export const MAX_PURCHASE_ITEMS = 100;
/** Tolerancia de `subtotal + tax` contra `total`. */
export const TOTAL_TOLERANCE = 0.01;

// --- Resultado -----------------------------------------------------------------------
export const VALIDATION_CODES = [
  "requerido",
  "no_valido",
  "fuera_de_rango",
  "muy_largo",
  "no_editable",
  "tipo_de_cambio_requerido",
  "fecha_anterior",
  "total_no_cuadra",
  "demasiados",
  "duplicado",
] as const;
export type ValidationCode = (typeof VALIDATION_CODES)[number];

export const VALIDATION_CODE_LABELS: Record<ValidationCode, Label> = {
  requerido: { es: "Es obligatorio", en: "Required" },
  no_valido: { es: "No es válido", en: "Invalid" },
  fuera_de_rango: { es: "Fuera de rango", en: "Out of range" },
  muy_largo: { es: "Es demasiado largo", en: "Too long" },
  no_editable: { es: "No se puede cambiar", en: "Cannot be changed" },
  tipo_de_cambio_requerido: { es: "Falta el tipo de cambio", en: "Exchange rate required" },
  fecha_anterior: { es: "La fecha es anterior a la de apertura", en: "Date is before the opening date" },
  total_no_cuadra: { es: "Subtotal más impuestos no da el total", en: "Subtotal plus tax does not match the total" },
  demasiados: { es: "Son demasiados elementos", en: "Too many items" },
  duplicado: { es: "Está repetido", en: "Duplicated" },
};

export interface ValidationIssue {
  /** Ruta del campo: `name`, `purchase.unitCost`, `items[2].serial`… */
  field: string;
  code: ValidationCode;
}

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; errors: ValidationIssue[] };

type Mode = "create" | "update";

class Collector {
  readonly errors: ValidationIssue[] = [];
  // Campo explícito (no `private readonly` en el constructor): la pantalla
  // compila este paquete con `erasableSyntaxOnly`.
  private readonly prefix: string;
  constructor(prefix = "") {
    this.prefix = prefix;
  }
  add(field: string, code: ValidationCode): void {
    this.errors.push({ field: this.prefix + field, code });
  }
  result<T>(value: T): ValidationResult<T> {
    return this.errors.length ? { ok: false, errors: this.errors } : { ok: true, value };
  }
}

const has = (o: object | null | undefined, k: string) => o != null && Object.prototype.hasOwnProperty.call(o, k);
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/** Texto opcional: recortado, vacío → null. `undefined` si no es texto. */
function text(c: Collector, field: string, v: unknown, max: number, required = false): string | null | undefined {
  if (v == null || (typeof v === "string" && v.trim() === "")) {
    if (required) c.add(field, "requerido");
    return null;
  }
  if (typeof v !== "string") {
    c.add(field, "no_valido");
    return undefined;
  }
  const t = v.trim();
  if (t.length > max) c.add(field, "muy_largo");
  return t;
}

function date(c: Collector, field: string, v: unknown, required = false): string | null {
  if (v == null || v === "") {
    if (required) c.add(field, "requerido");
    return null;
  }
  if (!isIsoDate(v)) {
    c.add(field, "no_valido");
    return null;
  }
  return v.slice(0, 10);
}

function amount(c: Collector, field: string, v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : NaN;
  if (!Number.isFinite(n)) {
    c.add(field, "no_valido");
    return null;
  }
  if (n < 0 || n > MAX_AMOUNT) c.add(field, "fuera_de_rango");
  return round2(n);
}

function rate(c: Collector, field: string, v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0 || v > 100_000) {
    c.add(field, "no_valido");
    return null;
  }
  return v;
}

function currency(c: Collector, field: string, v: unknown): string | null {
  if (v == null || v === "") return null;
  const cur = typeof v === "string" ? v.trim().toUpperCase() : "";
  if (!/^[A-Z]{3}$/.test(cur)) {
    c.add(field, "no_valido");
    return null;
  }
  return cur;
}

/** Moneda distinta de MXN con monto exige tipo de cambio. */
function needsRate(c: Collector, field: string, cur: string | null, amt: number | null, r: number | null): void {
  if (amt != null && cur && cur !== BASE_CURRENCY && r == null) c.add(field, "tipo_de_cambio_requerido");
}

function invoice(c: Collector, field: string, v: unknown): InvoiceRef | null {
  if (v == null) return null;
  if (!isObject(v)) {
    c.add(field, "no_valido");
    return null;
  }
  let uuid: string | null = null;
  if (v.uuid != null && v.uuid !== "") {
    uuid = typeof v.uuid === "string" ? normalizeInvoiceUuid(v.uuid) : "";
    if (!isValidInvoiceUuid(uuid)) c.add(`${field}.uuid`, "no_valido");
  }
  const serie = text(c, `${field}.serie`, v.serie, 40) ?? null;
  const folio = text(c, `${field}.folio`, v.folio, 40) ?? null;
  if (!uuid && !serie && !folio) return null;
  return { uuid, serie, folio };
}

function id(c: Collector, field: string, v: unknown): string | null {
  return text(c, field, v, MAX_ID_LENGTH) ?? null;
}

// --- Activo ---------------------------------------------------------------------------

export type AssetInputValue = AssetInput & {
  /** Serie normalizada; null si el activo no lleva serie. Solo si llegó `serial`. */
  identifierNorm?: string | null;
};

export function validateAssetInput(
  input: unknown,
  opts: { mode: Mode; canSeeCosts: boolean; fieldPrefix?: string },
): ValidationResult<AssetInputValue> {
  const c = new Collector(opts.fieldPrefix);
  const create = opts.mode === "create";
  if (!isObject(input)) {
    c.add("", "no_valido");
    return c.result({});
  }
  const out: AssetInputValue = {};

  if (create || has(input, "name")) {
    const name = text(c, "name", input.name, MAX_NAME_LENGTH, true);
    if (name) out.name = name;
  }
  if (create || has(input, "category")) {
    if (input.category == null || input.category === "") c.add("category", "requerido");
    else if (!isAssetCategory(input.category)) c.add("category", "no_valido");
    else out.category = input.category;
  }
  for (const k of ["brand", "model"] as const) {
    if (has(input, k)) {
      const v = text(c, k, input[k], MAX_TEXT_LENGTH);
      if (v !== undefined) out[k] = v;
    }
  }
  if (has(input, "serial")) {
    const serial = text(c, "serial", input.serial, MAX_TEXT_LENGTH);
    if (serial !== undefined) {
      const norm = serial ? normalizeSerial(serial) : "";
      if (serial && !norm) c.add("serial", "no_valido");
      if (norm.length > MAX_SERIAL_LENGTH) c.add("serial", "muy_largo");
      out.serial = serial;
      out.identifierNorm = norm || null;
    }
  }
  if (has(input, "notes")) {
    const notes = text(c, "notes", input.notes, MAX_NOTE_LENGTH);
    if (notes !== undefined) out.notes = notes;
  }

  if (has(input, "purchase") || create) {
    const p = input.purchase;
    if (p != null && !isObject(p)) c.add("purchase", "no_valido");
    const src = isObject(p) ? p : {};
    const purchase: NonNullable<AssetInput["purchase"]> = {};
    if (has(src, "purchaseId")) purchase.purchaseId = id(c, "purchase.purchaseId", src.purchaseId);
    if (has(src, "supplierId")) purchase.supplierId = id(c, "purchase.supplierId", src.supplierId);
    if (has(src, "date")) purchase.date = date(c, "purchase.date", src.date);
    if (has(src, "invoice")) purchase.invoice = invoice(c, "purchase.invoice", src.invoice);
    const cur = has(src, "currency") ? currency(c, "purchase.currency", src.currency) : null;
    if (cur || create) purchase.currency = cur ?? BASE_CURRENCY;
    if (opts.canSeeCosts) {
      const unitCost = has(src, "unitCost") ? amount(c, "purchase.unitCost", src.unitCost) : null;
      const r = has(src, "exchangeRate") ? rate(c, "purchase.exchangeRate", src.exchangeRate) : null;
      if (has(src, "unitCost")) purchase.unitCost = unitCost;
      if (has(src, "exchangeRate")) purchase.exchangeRate = r;
      needsRate(c, "purchase.exchangeRate", purchase.currency ?? null, unitCost, r);
    }
    if (Object.keys(purchase).length) out.purchase = purchase;
  }

  if (has(input, "warranty")) {
    const w = input.warranty;
    if (w != null && !isObject(w)) c.add("warranty", "no_valido");
    else out.warranty = { until: date(c, "warranty.until", isObject(w) ? w.until : null) };
  }

  if (has(input, "depreciation")) {
    const d = input.depreciation;
    if (d != null && !isObject(d)) c.add("depreciation", "no_valido");
    else if (isObject(d)) {
      const dep: NonNullable<AssetInput["depreciation"]> = {};
      if (has(d, "usefulLifeMonths")) {
        const m = d.usefulLifeMonths;
        if (m == null || m === "") dep.usefulLifeMonths = null;
        else if (typeof m !== "number" || !Number.isInteger(m)) c.add("depreciation.usefulLifeMonths", "no_valido");
        else if (m < 1 || m > MAX_USEFUL_LIFE_MONTHS) c.add("depreciation.usefulLifeMonths", "fuera_de_rango");
        else dep.usefulLifeMonths = m;
      }
      if (opts.canSeeCosts && has(d, "salvage")) dep.salvage = amount(c, "depreciation.salvage", d.salvage);
      if (Object.keys(dep).length) out.depreciation = dep;
    }
  }

  if (has(input, "maintenance")) {
    const m = input.maintenance;
    if (m != null && !isObject(m)) c.add("maintenance", "no_valido");
    else out.maintenance = { nextDueAt: date(c, "maintenance.nextDueAt", isObject(m) ? m.nextDueAt : null) };
  }

  for (const k of ["status", "assignment", "retiredAt", "retiredReason", "identifierNorm", "company_id", "source"]) {
    if (has(input, k)) c.add(k, "no_editable");
  }
  return c.result(out);
}

// --- Asignación ---------------------------------------------------------------------------

export type AssignInputValue =
  | { returnToStock: true }
  | { returnToStock: false; custodian: AssetCustodian | null; vehicleId: string | null; baseId: string | null; location: string | null };

export function validateAssignInput(input: unknown): ValidationResult<AssignInputValue> {
  const c = new Collector();
  if (!isObject(input)) {
    c.add("", "no_valido");
    return c.result({ returnToStock: true });
  }
  if (input.returnToStock === true) return c.result({ returnToStock: true });

  let custodian: AssetCustodian | null = null;
  if (input.custodian != null) {
    const k = input.custodian;
    if (!isObject(k) || (k.type != null && k.type !== "user")) c.add("custodian", "no_valido");
    else {
      const cid = text(c, "custodian.id", k.id, MAX_ID_LENGTH, true);
      custodian = cid
        ? {
            type: "user",
            id: cid,
            name: text(c, "custodian.name", k.name, MAX_NAME_LENGTH) ?? null,
            email: text(c, "custodian.email", k.email, MAX_NAME_LENGTH) ?? null,
            company_id: text(c, "custodian.company_id", k.company_id, MAX_ID_LENGTH) ?? null,
          }
        : null;
    }
  }
  const value: AssignInputValue = {
    returnToStock: false,
    custodian,
    vehicleId: id(c, "vehicleId", input.vehicleId),
    baseId: id(c, "baseId", input.baseId),
    location: text(c, "location", input.location, MAX_NAME_LENGTH) ?? null,
  };
  if (!c.errors.length && !value.custodian && !value.vehicleId && !value.baseId && !value.location) {
    c.add("", "requerido");
  }
  return c.result(value);
}

// --- Baja ------------------------------------------------------------------------------------

export function validateRetireInput(input: unknown): ValidationResult<RetireInput> {
  const c = new Collector();
  const src = isObject(input) ? input : {};
  if (!isObject(input)) c.add("", "no_valido");
  let reason: RetireInput["reason"] = "danado";
  if (src.reason == null || src.reason === "") c.add("reason", "requerido");
  else if (!isRetireReason(src.reason)) c.add("reason", "no_valido");
  else reason = src.reason;
  return c.result({
    reason,
    date: date(c, "date", src.date),
    note: text(c, "note", src.note, MAX_NOTE_LENGTH) ?? null,
  });
}

// --- Mantenimiento -------------------------------------------------------------------------

function maintenanceMoney(
  c: Collector,
  src: Record<string, unknown>,
  canSeeCosts: boolean,
): Pick<MaintenanceOpenInput, "cost" | "currency" | "exchangeRate"> {
  const cur = currency(c, "currency", src.currency);
  if (!canSeeCosts) return cur ? { currency: cur } : {};
  const cost = amount(c, "cost", src.cost);
  const r = rate(c, "exchangeRate", src.exchangeRate);
  needsRate(c, "exchangeRate", cur ?? BASE_CURRENCY, cost, r);
  return { cost, currency: cur ?? BASE_CURRENCY, exchangeRate: r };
}

export function validateMaintenanceOpenInput(
  input: unknown,
  opts: { canSeeCosts: boolean },
): ValidationResult<MaintenanceOpenInput> {
  const c = new Collector();
  const src = isObject(input) ? input : {};
  if (!isObject(input)) c.add("", "no_valido");
  let kind: MaintenanceOpenInput["kind"] = "correctivo";
  if (src.kind == null || src.kind === "") c.add("kind", "requerido");
  else if (!isMaintenanceKind(src.kind)) c.add("kind", "no_valido");
  else kind = src.kind;
  return c.result({
    kind,
    openedAt: date(c, "openedAt", src.openedAt),
    supplierId: id(c, "supplierId", src.supplierId),
    description: text(c, "description", src.description, MAX_NOTE_LENGTH) ?? null,
    ...maintenanceMoney(c, src, opts.canSeeCosts),
    invoice: invoice(c, "invoice", src.invoice),
    nextDueAt: date(c, "nextDueAt", src.nextDueAt),
  });
}

export function validateMaintenanceCloseInput(
  input: unknown,
  opts: { canSeeCosts: boolean; openedAt: string | null },
): ValidationResult<MaintenanceCloseInput> {
  const c = new Collector();
  const src = isObject(input) ? input : {};
  if (!isObject(input)) c.add("", "no_valido");
  const closedAt = date(c, "closedAt", src.closedAt);
  if (closedAt && opts.openedAt && isIsoDate(opts.openedAt) && closedAt < opts.openedAt.slice(0, 10)) {
    c.add("closedAt", "fecha_anterior");
  }
  const value: MaintenanceCloseInput = { closedAt, ...maintenanceMoney(c, src, opts.canSeeCosts) };
  if (has(src, "invoice")) value.invoice = invoice(c, "invoice", src.invoice);
  if (has(src, "description")) value.description = text(c, "description", src.description, MAX_NOTE_LENGTH) ?? null;
  if (has(src, "nextDueAt")) value.nextDueAt = date(c, "nextDueAt", src.nextDueAt);
  return c.result(value);
}

// --- Proveedor -------------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateSupplierInput(
  input: unknown,
  opts: { mode: Mode; currentRfc?: string | null },
): ValidationResult<SupplierInput> {
  const c = new Collector();
  const create = opts.mode === "create";
  if (!isObject(input)) {
    c.add("", "no_valido");
    return c.result({});
  }
  const out: SupplierInput = {};
  if (create || has(input, "name")) {
    const name = text(c, "name", input.name, MAX_NAME_LENGTH, true);
    if (name) out.name = name;
  }
  if (has(input, "rfc")) {
    const rfc = input.rfc == null || input.rfc === "" ? "" : typeof input.rfc === "string" ? normalizeRfc(input.rfc) : null;
    if (rfc === null || (rfc && !isValidRfc(rfc))) c.add("rfc", "no_valido");
    else if (!create) {
      // El RFC no se edita: solo se acepta el mismo que ya tiene.
      if (rfc !== normalizeRfc(opts.currentRfc)) c.add("rfc", "no_editable");
    } else out.rfc = rfc || null;
  }
  if (create || has(input, "roles")) {
    const roles = input.roles;
    if (!Array.isArray(roles) || roles.length === 0) c.add("roles", "requerido");
    else if (!roles.every(isSupplierRole)) c.add("roles", "no_valido");
    else out.roles = [...new Set(roles as SupplierRole[])];
  }
  if (has(input, "contact")) {
    const k = input.contact;
    if (k == null) out.contact = null;
    else if (!isObject(k)) c.add("contact", "no_valido");
    else {
      const email = text(c, "contact.email", k.email, MAX_NAME_LENGTH) ?? null;
      if (email && !EMAIL_RE.test(email)) c.add("contact.email", "no_valido");
      out.contact = {
        name: text(c, "contact.name", k.name, MAX_NAME_LENGTH) ?? null,
        email: email ? email.toLowerCase() : null,
        phone: text(c, "contact.phone", k.phone, 30) ?? null,
      };
    }
  }
  return c.result(out);
}

// --- Compra ------------------------------------------------------------------------------------

export type PurchaseInputValue = Omit<PurchaseInput, "items" | "invoice"> & {
  invoice?: InvoiceRef | null;
  items?: AssetInputValue[];
};

export function validatePurchaseInput(
  input: unknown,
  opts: { mode: Mode; canSeeCosts: boolean },
): ValidationResult<PurchaseInputValue> {
  const c = new Collector();
  const create = opts.mode === "create";
  if (!isObject(input)) {
    c.add("", "no_valido");
    return c.result({});
  }
  const out: PurchaseInputValue = {};
  if (has(input, "supplierId")) out.supplierId = id(c, "supplierId", input.supplierId);
  if (create || has(input, "date")) out.date = date(c, "date", input.date, true);
  if (has(input, "invoice")) out.invoice = invoice(c, "invoice", input.invoice);
  const cur = has(input, "currency") ? currency(c, "currency", input.currency) : null;
  if (cur || create) out.currency = cur ?? BASE_CURRENCY;

  if (opts.canSeeCosts) {
    for (const k of ["subtotal", "tax", "total"] as const) {
      if (has(input, k)) out[k] = amount(c, k, input[k]);
    }
    if (has(input, "exchangeRate")) out.exchangeRate = rate(c, "exchangeRate", input.exchangeRate);
    needsRate(c, "exchangeRate", out.currency ?? null, out.total ?? out.subtotal ?? null, out.exchangeRate ?? null);
    const { subtotal, tax, total } = out;
    if (subtotal != null && total != null && Math.abs(round2(subtotal + (tax ?? 0)) - total) > TOTAL_TOLERANCE) {
      c.add("total", "total_no_cuadra");
    }
  }

  if (!create) {
    if (has(input, "items")) c.add("items", "no_editable");
  } else if (!Array.isArray(input.items) || input.items.length === 0) {
    c.add("items", "requerido");
  } else if (input.items.length > MAX_PURCHASE_ITEMS) {
    c.add("items", "demasiados");
  } else {
    out.items = input.items.map((item, i) => {
      const r = validateAssetInput(item, { mode: "create", canSeeCosts: opts.canSeeCosts, fieldPrefix: `items[${i}].` });
      if (!r.ok) {
        c.errors.push(...r.errors);
        return {};
      }
      return r.value;
    });
    // Dos piezas de la misma compra con la misma serie.
    const seen = new Map<string, number>();
    out.items.forEach((item, i) => {
      const norm = item.identifierNorm;
      if (!norm) return;
      if (seen.has(norm)) c.add(`items[${i}].serial`, "duplicado");
      else seen.set(norm, i);
    });
  }
  return c.result(out);
}
